import { AppConstants } from "@/common";
import { TimeUtils } from "@/utils/time";

const LAST_BACKGROUNDED_AT_KEY = 'appLastBackgroundedAt';

/**
 * Determines whether the app was away (backgrounded/inactive) for longer than the allowed
 * threshold and should therefore be reset to the Home Screen on resume (LGSAM-469).
 *
 * @param {number | null} lastBackgroundedAt timestamp (ms) recorded when the app went to background, or null if not recorded
 * @param {number} nowMs current timestamp (ms)
 * @param {number} thresholdMs away-duration threshold in milliseconds, defaults to AppConstants.appResumeAwayThresholdMs
 * @returns {boolean} true when elapsed time is strictly greater than the threshold
 */
export const shouldForceHomeReset = (lastBackgroundedAt: number | null, nowMs: number, thresholdMs: number = AppConstants.appResumeAwayThresholdMs): boolean => {
    if (lastBackgroundedAt === null) {
        return false;
    }

    return (nowMs - lastBackgroundedAt) > thresholdMs;
};

/**
 * Tracks how long the app was backgrounded/inactive so a caller can decide whether to force
 * a reset to the Home Screen on resume (LGSAM-469). The "last backgrounded at" timestamp is
 * persisted via `$domains.lsCacher` (local-storage backed) so it survives the JS context being
 * torn down while the app is backgrounded/asleep.
 */
export const useAppResumeTimer = () => {
    const { $domains } = useNuxtApp();

    /**
     * Persists the current time as the last moment the app went to background/inactive.
     * Always overwrites any previously recorded value, so only the single most recent
     * background timestamp is ever used (AC #9 - no accumulation across short cycles).
     */
    const recordBackgrounded = (): void => {
        $domains.lsCacher.put<number>(LAST_BACKGROUNDED_AT_KEY, TimeUtils.dateNow());
    };

    /**
     * Reads the persisted "last backgrounded at" timestamp, clears it for the next cycle,
     * and returns whether the elapsed away-duration exceeds the reset threshold.
     *
     * @returns {boolean} true when the app was away for longer than the allowed threshold
     */
    const consumeAwayDurationExceedsThreshold = (): boolean => {
        const lastBackgroundedAt = $domains.lsCacher.get<number>(LAST_BACKGROUNDED_AT_KEY);
        $domains.lsCacher.del(LAST_BACKGROUNDED_AT_KEY);

        return shouldForceHomeReset(lastBackgroundedAt, TimeUtils.dateNow());
    };

    return {
        recordBackgrounded,
        consumeAwayDurationExceedsThreshold,
    };
};
